import { injectable, inject } from 'inversify';
import { DesignImplementationFlow } from "../../domain/aggregates/DesignImplementationFlow";
import { DesignImplementationFlowRepository } from "../../infrastructure/repositories/DesignImplementationFlowRepository";
import { 
  CreateFlowCommand, 
  CreateFlowResult 
} from "../commands/CreateFlowCommand";
import { 
  AddArtifactCommand, 
  AddArtifactResult 
} from "../commands/AddArtifactCommand";
import { TYPES } from '../../../../types/DITypes';

/**
 * Design Implementation Flow Application Service
 * 
 * Following Eric Evans Application Service pattern for coordination management.
 * Coordinates flow operations following DDD principles.
 * 
 * Rule: Every flow must be linked to a GTD project for proper coordination.
 */
@injectable()
export class DesignImplementationFlowApplicationService {
  constructor(
    @inject(TYPES.DesignImplementationFlowRepository) 
    private readonly flowRepository: DesignImplementationFlowRepository
  ) {}

  /**
   * Create a new design implementation flow
   * 
   * Principle: Flows coordinate design-to-implementation tracking
   * for multi-artifact outcomes.
   */
  async createFlow(command: CreateFlowCommand): Promise<CreateFlowResult> {
    try {
      // Create the flow aggregate
      const flow = DesignImplementationFlow.create(
        command.projectId,
        command.createdBy,
        command.metaModelBinding,
        command.designSpecification
      );

      // Persist the flow
      await this.flowRepository.save(flow);

      return {
        success: true,
        flowId: flow.id,
        createdAt: flow.createdAt.toISOString(),
        message: `Design implementation flow created successfully for project ${command.projectId}`
      };
    } catch (error) {
      return {
        success: false,
        flowId: '',
        createdAt: new Date().toISOString(),
        message: error instanceof Error ? error.message : 'Failed to create flow'
      };
    }
  }

  /**
   * Add artifact to existing flow
   * 
   * Principle: Artifacts are the units of implementation being tracked
   */
  async addArtifact(command: AddArtifactCommand): Promise<AddArtifactResult> {
    try {
      const flow = await this.flowRepository.findById(command.flowId);
      
      if (!flow) {
        return {
          success: false,
          artifactId: '',
          flowId: command.flowId,
          createdAt: new Date().toISOString(),
          message: `Flow ${command.flowId} not found`
        };
      }

      const artifactId = flow.addArtifact(
        command.name,
        command.type,
        command.location,
        command.createdBy,
        command.version,
        command.tags || []
      );

      await this.flowRepository.save(flow);

      return {
        success: true,
        artifactId,
        flowId: command.flowId,
        createdAt: new Date().toISOString(),
        message: `Artifact "${command.name}" added to flow successfully`
      };
    } catch (error) {
      return {
        success: false,
        artifactId: '',
        flowId: command.flowId,
        createdAt: new Date().toISOString(),
        message: error instanceof Error ? error.message : 'Failed to add artifact'
      };
    }
  }

  /**
   * Update artifact conformance score
   */
  async updateConformanceScore(
    flowId: string, 
    artifactId: string,
    metaModelScore: number,
    designCoverageScore: number,
    confidence: 'high' | 'medium' | 'low',
    updatedBy: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const flow = await this.flowRepository.findById(flowId);
      
      if (!flow) {
        return {
          success: false,
          message: `Flow ${flowId} not found`
        };
      }

      flow.updateConformanceScore(
        artifactId, 
        metaModelScore, 
        designCoverageScore,
        confidence, 
        updatedBy
      );
      await this.flowRepository.save(flow);

      return {
        success: true,
        message: `Conformance score updated successfully`
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update conformance score'
      };
    }
  }

  /**
   * Update artifact status
   */
  async updateArtifactStatus(
    flowId: string,
    artifactId: string,
    newStatus: string,
    updatedBy: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const flow = await this.flowRepository.findById(flowId);
      
      if (!flow) {
        return {
          success: false,
          message: `Flow ${flowId} not found`
        };
      }

      flow.updateArtifactStatus(artifactId, newStatus, updatedBy);
      await this.flowRepository.save(flow);

      return {
        success: true,
        message: `Artifact status updated to ${newStatus}`
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update artifact status'
      };
    }
  }

  /**
   * Complete flow
   */
  async completeFlow(flowId: string, completedBy: string): Promise<{ success: boolean; message: string }> {
    try {
      const flow = await this.flowRepository.findById(flowId);
      
      if (!flow) {
        return {
          success: false,
          message: `Flow ${flowId} not found`
        };
      }

      flow.complete(completedBy);
      await this.flowRepository.save(flow);

      return {
        success: true,
        message: `Flow completed successfully`
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to complete flow'
      };
    }
  }

  /**
   * Activate flow
   */
  async activateFlow(flowId: string): Promise<{ success: boolean; message: string }> {
    try {
      const flow = await this.flowRepository.findById(flowId);
      
      if (!flow) {
        return {
          success: false,
          message: `Flow ${flowId} not found`
        };
      }

      flow.activate();
      await this.flowRepository.save(flow);

      return {
        success: true,
        message: `Flow activated successfully`
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to activate flow'
      };
    }
  }
}